import { Component, h, Prop, State } from '@stencil/core';
import { getAuthClient, AuthClientInstance } from '@trumpf/ux.utils';
import { fetchChats, addChat, IChat, getCaseStatusString } from '../../backend/chatService';

@Component({
  tag: 'ai-chat-wrapper',
  styleUrl: 'ai-chat-wrapper.scss',
  shadow: true,
})
export class AiChatWrapper {
  private authClient: AuthClientInstance;
  private caseStatusInterval: NodeJS.Timeout | null = null;

  @Prop() caseId!: string;
  @Prop() sessionId!: string;
  @Prop() token?: string;

  @State() chat: IChat | null = null;
  @State() caseStatus: string = 'loading'; // New state for case status
  @State() loading: boolean = false;
  @State() error: string | null = null;

  constructor() {
    this.authClient = getAuthClient();
  }

  async componentWillLoad() {
    if (!this.token) {
      await this.retrieveToken();
    }
    await this.loadChat();
    await this.loadCaseStatus();
    this.startCaseStatusSubscription(); // Start polling case status
  }

  disconnectedCallback() {
    this.stopCaseStatusSubscription(); // Cleanup when component is removed
  }

  private async retrieveToken() {
    try {
      let token = sessionStorage.getItem('auth_token');

      if (!token) {
        token = this.authClient.getAccessToken();
        if (!token) {
          token = await this.waitForToken();
        }
      }

      if (token) {
        this.token = token;
        sessionStorage.setItem('auth_token', token);
      } else {
        console.warn('Token is unavailable after waiting.');
      }
    } catch (error) {
      console.error('Error retrieving access token:', error);
      this.error = 'Authentication failed';
    }
  }

  private async waitForToken(maxRetries = 10, delayMs = 200): Promise<string | null> {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const token = this.authClient.getAccessToken();
      if (token) return token;
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
    return null;
  }

  private async loadChat() {
    if (!this.caseId || !this.sessionId) {
      this.error = 'Case ID and Session ID are required';
      return;
    }

    try {
      this.loading = true;
      const chats = await fetchChats(this.caseId);
      this.chat = chats.find(chat => chat.sessionId === this.sessionId) || null;
      console.log('Chat:', this.chat);

      if (!this.chat) {
        await this.createNewChat();
      }
    } catch (error) {
      console.error('Error loading chat:', error);
      this.error = 'Failed to load chat';
    } finally {
      this.loading = false;
    }
  }

  private async loadCaseStatus() {
    if (!this.caseId) {
      console.warn('No caseId provided for fetching case status');
      return;
    }

    try {
      const newStatus = await getCaseStatusString(this.caseId);

      console.log(`[loadCaseStatus] Current: "${this.caseStatus}", New: "${newStatus}"`); // Log current and new

      if (newStatus !== this.caseStatus) {
        console.log(`Updating case status from "${this.caseStatus}" to "${newStatus}"`);
        this.caseStatus = newStatus; // Reactively updating Stencil state

        // Force Stencil to recognize the state change and re-render
        requestAnimationFrame(() => {
          this.caseStatus = newStatus;
        });
      } else {
        console.log(`[loadCaseStatus] Status is unchanged: "${this.caseStatus}"`);
      }
    } catch (error) {
      console.error('Error fetching case status:', error);
      this.caseStatus = 'unknown';
    }
  }

  private async createNewChat() {
    try {
      this.chat = await addChat(this.caseId, 'User', 'User'); // We might want to make author/group configurable
    } catch (error) {
      console.error('Error creating new chat:', error);
      this.error = 'Failed to create new chat';
    }
  }

  private async handleLoadChat() {
    try {
      await this.delay(2000); // 2-second delay
      await this.loadChat();
    } catch (error) {
      console.error('Error loading the chat:', error);
    }
  }

  //Todo: Remove this later
  private async delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Polling mechanism: Fetches case status every 10 seconds
  private startCaseStatusSubscription() {
    if (!this.caseId) return;

    console.log(`[startCaseStatusSubscription] Polling started for caseId: ${this.caseId}`); // Confirm polling starts

    this.caseStatusInterval = setInterval(async () => {
      console.log(`[startCaseStatusSubscription] Polling case status...`);
      await this.loadCaseStatus();
    }, 30000); // Fetch case status every 30 seconds
  }

  // Cleanup interval on component unload
  private stopCaseStatusSubscription() {
    if (this.caseStatusInterval) {
      clearInterval(this.caseStatusInterval);
      this.caseStatusInterval = null;
    }
  }

  render() {
    if (this.error) {
      return (
        <div class="error-state">
          <ux-icon name="warning" size="large"></ux-icon>
          <p>{this.error}</p>
        </div>
      );
    }

    if (this.loading) {
      return (
        <div class="loading-state">
          <ux-loader variant="horizontal"></ux-loader>
        </div>
      );
    }

    return (
      <div class="ai-chat-wrapper">
        {this.chat ? (
          <div>
            <ai-chat token={this.token} data={this.chat} caseId={this.caseId} caseStatus={this.caseStatus} onDisclaimerSubmitted={() => this.handleLoadChat()}></ai-chat>
          </div>
        ) : (
          <div class="empty-state">
            <ux-icon name="chat" size="large"></ux-icon>
            <p>No chat available</p>
          </div>
        )}
      </div>
    );
  }
}
